import { useEffect } from 'react';
import { FormikProps, useFormik } from 'formik';
import * as Yup from 'yup';
import { EZoneProduct, EZoneTargetCleaningType } from 'enums';

import { IBuilding, IZone } from 'models';
import { useCreateZoneMutation, useUpdateZoneMutation } from 'store/apis';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';
import { useAppSelector } from 'store/store.hook';
import { convertMinutesToStringFormat } from 'utils';

interface UseZoneFormManagerProps {
  building?: IBuilding | null;
  zone?: IZone | null;
  next?: (data?: any) => void;
}

export const useZoneFormManager = ({ building = null, zone = null, next = () => {} }: UseZoneFormManagerProps) => {
  const { orgSlug } = useAppSelector(selectCurrentUserInfo);

  const useMutation = zone ? useUpdateZoneMutation : useCreateZoneMutation;
  const [mutate, { isLoading, isSuccess, isError, ...rest }] = useMutation();

  const initialValues = {
    name: zone?.name || '',
    clean_code_zone_category_id: zone?.clean_code_zone_category_id ?? null,
    building_id: building?.id ?? null,
    building_area_id: zone?.building_area_id ?? null,
    building_floor_id: zone?.building_floor_id ?? null,
    target_cleaning_type: zone?.target_cleaning_type ?? 'none',
    target_interval: zone?.target_interval ?? 'daily',
    target_frequency: zone?.target_frequency ?? 1,
    cleaning_interval_seconds: (zone?.cleaning_interval_seconds ?? 0) / 3600,
    days_active: zone?.days_active ?? '1,2,3,4,5', // default monday through friday
    schedule: [480],
    product: zone?.product ?? EZoneProduct.BASIC,
  };

  const validationSchema = Yup.object({
    name: Yup.string().nullable().required('Zone name is required.'),
    clean_code_zone_category_id: Yup.number().nullable().required('Category must be selected.'),
    building_area_id: Yup.number().nullable().required('Building Area must be selected.'),
    building_id: Yup.number().nullable().required('Building must be selected.'),
    building_floor_id: Yup.number().nullable().required('Floor must be selected.'),
    product: Yup.string().nullable().required('Zone Type is required.'),
    target_cleaning_type: Yup.string().required('Service Target is required.'),
    target_interval: Yup.string().nullable(),
    target_frequency: Yup.number().nullable(),
    schedule: Yup.array().when('target_cleaning_type', {
      is: EZoneTargetCleaningType.SCHEDULE,
      then: Yup.array().ensure().of(Yup.string()).min(1, 'At least one cleaning time must be added.'),
      otherwise: Yup.array(),
    }),
  });

  const formManager = useFormik({
    enableReinitialize: false,
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      const {
        name,
        building_area_id,
        clean_code_zone_category_id,
        building_id,
        building_floor_id,
        target_cleaning_type,
        target_interval,
        target_frequency,
        product,
        cleaning_interval_seconds,
        schedule = [],
        days_active,
      } = values;

      const newZone = {
        name: name.trim(),
        building_area_id,
        clean_code_zone_category_id,
        building_id,
        building_floor_id,
        product,
        target_cleaning_type,
        target_interval,
        target_frequency,
        cleaning_interval_seconds: cleaning_interval_seconds ? cleaning_interval_seconds * 3600 : 0,
        schedule: [],
        days_active,
      } as any;

      if (target_cleaning_type === EZoneTargetCleaningType.SCHEDULE) {
        newZone['schedule'] = schedule.map((s: any) => convertMinutesToStringFormat(s, false, true));
      }

      let mutateProps: any = {
        orgSlug,
        zone: newZone,
      };

      if (zone) {
        mutateProps = { ...mutateProps, id: zone.id };
      }

      mutate(mutateProps);
    },
  });

  useEffect(() => {
    if (!isLoading && isSuccess && !isError) {
      const { data } = rest;

      next(data);
    }
  }, [isLoading, isSuccess, isError]);

  return [
    {
      formManager,
      loading_submit: isLoading,
    },
  ] as [
    {
      formManager: FormikProps<typeof initialValues>;
      loading_submit: boolean;
    },
  ];
};
